/**
 * The sign-in form, and nothing else.
 *
 * It takes the API and a success callback as props rather than reaching for
 * context, so a test can render it alone with a stub and assert exactly what it
 * asked for. Deciding *where* to go afterwards is the page's job — see
 * `Login.tsx`.
 */

import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";

import { AuthError, type AuthApi } from "../../auth/authApi";
import { Field, firstMissing } from "./AuthCard";

interface Props {
  api: AuthApi;
  onSignedIn: () => void | Promise<void>;
  /** Rendered above the fields — used for "your password has been changed". */
  notice?: string;
}

export function LoginForm({ api, onSignedIn, notice }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;

    const missing = firstMissing([
      { label: "Email address", value: email },
      { label: "Password", value: password },
    ]);
    if (missing) {
      setError(missing);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.signIn({ email, password });
      await onSignedIn();
    } catch (cause) {
      setError(explain(cause));
      // Only on failure. On success this component is usually unmounted by the
      // navigation that follows, and setting state afterwards would be a
      // warning about updating something that no longer exists.
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      {notice && (
        <p className="auth__notice" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="auth__error" role="alert">
          {error}
        </p>
      )}

      <Field
        id="email"
        label="Email address"
        type="email"
        value={email}
        autoComplete="email"
        onChange={setEmail}
      />
      <Field
        id="password"
        label="Password"
        type="password"
        value={password}
        autoComplete="current-password"
        onChange={setPassword}
      />

      <button type="submit" className="button button--primary auth__submit" disabled={busy}>
        {busy ? "Signing in…" : "Sign in"}
      </button>

      <p className="auth__aside">
        <Link to="/forgot-password">Forgotten your password?</Link>
      </p>
    </form>
  );
}

/**
 * Turns a refusal into something worth reading.
 *
 * Only one case is rewritten, and it earns it: the API answers an unverified
 * address with "Email not verified", which next to a password field reads as a
 * credentials problem. Someone who takes it that way goes and resets a password
 * that was never wrong. Everything else is the server's own wording, because
 * inventing a friendlier sentence here is how a UI ends up describing a failure
 * that did not happen.
 */
function explain(cause: unknown): string {
  if (cause instanceof AuthError) {
    if (cause.code === "EMAIL_NOT_VERIFIED") {
      // A fresh link goes out on every sign-in attempt that gets this far
      // (`emailVerification.sendOnSignIn`), so this points at the new one
      // rather than the one from sign-up, which may well have expired — an
      // hour is all they last. Pointing at the old link was the difference
      // between a recoverable state and a dead end.
      return "Confirm your email address first — we've just sent a fresh link to your inbox.";
    }
    return cause.message;
  }
  return "Could not reach the server. Check your connection and try again.";
}
